import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'large' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const timeAgo = getTimeAgo(article.publishedAt);

  if (variant === 'large') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
      >
        <div className="relative w-full h-[280px] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={`911黑料网爆料 - ${article.title}`}
            title={`911黑料网独家爆料 - ${article.title}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute top-3 left-3 flex gap-2">
            {article.isExclusive && (
              <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded whitespace-nowrap">
                独家
              </span>
            )}
            {article.isHot && (
              <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded whitespace-nowrap">
                热门
              </span>
            )}
          </div>
        </div>
        <div className="p-4">
          <span className="inline-block px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded mb-2 whitespace-nowrap">
            {article.category}
          </span>
          <h3 className="font-heading font-semibold text-foreground-900 text-lg leading-snug mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
            {article.excerpt}
          </p>
          <div className="flex items-center justify-between text-xs text-foreground-500">
            <span>{article.author}</span>
            <div className="flex items-center gap-3">
              <span>{timeAgo}</span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatViews(article.views)}
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-3 py-3 border-b border-background-200/70 last:border-b-0"
      >
        <div className="relative w-24 h-16 shrink-0 rounded-md overflow-hidden">
          <img
            src={article.imageUrl}
            alt={`911黑料网爆料 - ${article.title}`}
            title={`911黑料网独家爆料 - ${article.title}`}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="min-w-0">
          <h4 className="font-heading font-medium text-foreground-900 text-sm leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h4>
          <div className="flex items-center gap-2 text-xs text-foreground-500 mt-1 whitespace-nowrap">
            <span>{timeAgo}</span>
            <span>·</span>
            <span>{formatViews(article.views)} 阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
    >
      <div className="relative w-full h-[200px] overflow-hidden">
        <img
          src={article.imageUrl}
          alt={`911黑料网爆料 - ${article.title}`}
          title={`911黑料网独家爆料 - ${article.title}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {article.isExclusive && (
          <div className="absolute top-2 left-2">
            <span className="px-1.5 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded whitespace-nowrap">
              独家
            </span>
          </div>
        )}
      </div>
      <div className="p-3">
        <span className="inline-block px-1.5 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded mb-2 whitespace-nowrap">
          {article.category}
        </span>
        <h3 className="font-heading font-semibold text-foreground-900 text-sm leading-snug mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2 mb-2">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span>{timeAgo}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(article.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}

function getTimeAgo(dateStr: string): string {
  const now = Date.now();
  const diff = now - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 60) return `${Math.max(minutes, 1)}分钟前`;
  if (hours < 24) return `${hours}小时前`;
  if (days < 30) return `${days}天前`;
  return new Date(dateStr).toLocaleDateString('zh-CN');
}

function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return views.toString();
}